import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../api';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const convertSettings = (settings) => {
    if (!settings) return {};

    const {
        dots,
        speed,
        arrows,
        autoplay,
        infinite,
        slidesToShow,
        autoplaySpeed,
        slidesToScroll,
        responsive
    } = settings;

    const converted = {
        dots: dots === '1',
        speed: Number(speed),
        arrows: arrows === '1',
        autoplay: autoplay === '1',
        infinite: infinite === '1',
        slidesToShow: Number(slidesToShow),
        autoplaySpeed: Number(autoplaySpeed),
        slidesToScroll: Number(slidesToScroll),
        responsive: Array.isArray(responsive)
            ? responsive.map(r => ({
                breakpoint: Number(r.breakpoint),
                settings: {
                    slidesToShow: Number(r.settings.slidesToShow),
                    slidesToScroll: Number(r.settings.slidesToScroll)
                }
            }))
            : []
    };
    return converted;
};

const LogoItem = ({ logo }) => (
    <div className="item">
        <a
            href={logo.link}
            rel="noopener noreferrer"
            {...(logo.link && logo.link !== "#" ? { target: "_blank" } : {})}
        >
            <img
                src={`${process.env.REACT_APP_API_URL}/public${logo.image}`}
                alt={logo.alt}
                className="img-fluid mx-auto"
            />
        </a>
    </div>
);

// Main Section Component
const LogoSection = () => {
    const { miniSlug, slug } = useParams();
    const [logosData, setLogosData] = useState([]);
    const [industryData, setIndustryData] = useState([]);
    const [industryTitle, setIndustryTitle] = useState('');
    const [sliderSettings, setSliderSettings] = useState({
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 2,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 3000,
        arrows: false,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1
                }
            }
        ]
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchLogoData = async () => {
            try {
                setError(false);

                const response = await api.get(`${process.env.REACT_APP_API_URL}/api/${miniSlug}/${slug}/logos`);
                const data = response.data;

                const mainLogos = data.logos?.logos || [];
                setLogosData(mainLogos);

                const industryLogos = data.logos?.industry || [];
                setIndustryData(industryLogos);
                setIndustryTitle(data.logos?.industry_title || '');

                if (data.settings) {
                    const convertedSettings = convertSettings(data.settings);

                    setSliderSettings(prev => ({
                        ...prev,
                        ...convertedSettings,
                        responsive: [
                            {
                                breakpoint: 768,
                                settings: {
                                    slidesToShow: 1,
                                    slidesToScroll: 1
                                }
                            },
                            ...(convertedSettings.responsive || []).filter(r => r.breakpoint !== 768)
                        ]
                    }));
                }

            } catch (error) {
                console.error('Error fetching logo data:', error);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchLogoData();
    }, [miniSlug, slug]);

    if (loading || error) {
        return <></>;
    }

    const hasLogos = logosData.length > 0;
    const hasIndustry = industryData.length > 0;

    if (!hasLogos && !hasIndustry) {
        return <></>;
    }

    return (
        <>
            {hasLogos && (
                <section className="logos_section mb-5 ">
                    <div className="container">
                        <div className="row">
                            <div className="sec-title text-center">
                            <h2>Our Partners</h2>
                        </div>
                            
                            {logosData.length === 1 ? (
                                <div className="single-logo d-flex justify-content-center">
                                    <LogoItem logo={logosData[0]} />
                                </div>
                            ) : (
                                <Slider {...sliderSettings} dots={false} className="logos-carousel">
                                    {logosData.map((logo, index) => (
                                        <LogoItem key={`logo-${index}`} logo={logo} />
                                    ))}
                                </Slider>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {hasIndustry && (
                <section className="industry_section py-4">
                    <div className="container">
                        <div className="row">
                            <h2 className="text-center mb-4">{industryTitle || 'Industry Partners'}</h2>
                            {industryData.length === 1 ? (
                                <div className="single-logo d-flex justify-content-center">
                                    <LogoItem logo={industryData[0]} />
                                </div>
                            ) : (
                                <Slider {...sliderSettings} dots={false} className="industry-carousel">
                                    {industryData.map((logo, index) => (
                                        <LogoItem key={`industry-${index}`} logo={logo} />
                                    ))}
                                </Slider>
                            )}
                        </div>
                    </div>
                </section>
            )}
        </>
    );
};

export default LogoSection;